import React, { useMemo } from "react";
import SelectMenu from "../shared/SelectMenu";

// GroupSelect — the competitor-group dropdown for the absorption-trends
// "competitor group" filter.
//
// The parent slices ci_get_available_filters' `category_details` down to the
// active category tab and passes that per-category object as `categoryDetails`;
// this component reads `properties_by_tier` off it. (The prop list is
// `(categoryDetails, selectedGroup, onChange)` with no `tab` prop, so the
// slicing happens upstream — same way MarketPositionPanel receives a scoped
// `categoryCode` rather than the whole filters envelope.)
//
// `properties_by_tier` may arrive as either a `{ tier: count }` map or an
// array of `{ tier, count }` rows — the live shape isn't confirmed in the repo
// yet, so we normalize both. Labels follow the tracker: direct → "Your
// competitors", airbnb → "Airbnb", hotel → "Hotels", glampinghub → "Glamping
// Hub", public → "Public campgrounds". Each option shows its property count,
// e.g. "Airbnb (84)".

const GROUP_LABELS = {
  direct: "Your competitors",
  airbnb: "Airbnb",
  hotel: "Hotels",
  glampinghub: "Glamping Hub",
  public: "Public campgrounds",
};

// Stable left-to-right display order so the dropdown doesn't reshuffle as the
// counts behind it change.
const GROUP_ORDER = ["direct", "airbnb", "hotel", "glampinghub", "public"];

const labelFor = (tier) =>
  GROUP_LABELS[tier] ?? (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "");

const GroupSelect = ({ categoryDetails, selectedGroup, onChange }) => {
  const options = useMemo(() => {
    const byTier = categoryDetails?.properties_by_tier;
    if (!byTier) return [];

    // Normalize to [{ tier, count }] regardless of the live shape.
    const rows = Array.isArray(byTier)
      ? byTier.map((r) => ({ tier: r?.tier, count: Number(r?.count ?? r?.properties ?? 0) || 0 }))
      : Object.entries(byTier).map(([tier, value]) => ({
          tier,
          count:
            Number(
              (value && typeof value === "object" ? value?.count ?? value?.properties : value) ?? 0,
            ) || 0,
        }));

    return rows
      .filter((r) => r.tier)
      .sort(
        (a, b) =>
          (GROUP_ORDER.indexOf(a.tier) === -1 ? GROUP_ORDER.length : GROUP_ORDER.indexOf(a.tier)) -
          (GROUP_ORDER.indexOf(b.tier) === -1 ? GROUP_ORDER.length : GROUP_ORDER.indexOf(b.tier)),
      )
      .map((r) => ({
        value: r.tier,
        label: `${labelFor(r.tier)} (${r.count})`,
      }));
  }, [categoryDetails]);

  if (options.length === 0) return null;

  return (
    <SelectMenu
      options={options}
      value={selectedGroup}
      onChange={onChange}
      ariaLabel="Competitor group"
    />
  );
};

export default GroupSelect;